/**
 * useProfileSettings — read/write the v2 profile settings via
 * `GET/PATCH /api/v1/profile/settings`.
 *
 * Optimistic update: `setMode` / `setChatLayoutPref` write into the React
 * Query cache synchronously, then PATCH the server. On PATCH failure
 * (or fetch rejection) the cache is reverted to the per-call snapshot
 * captured before the optimistic write, and the returned promise rejects
 * so callers can show a toast or retry UI.
 *
 * Race-safety:
 *   - `cancelQueries` is awaited before each optimistic write so an
 *     in-flight initial GET cannot land between the optimistic write and
 *     the PATCH and clobber the new value.
 *   - The revert only fires if the cache still holds the value this call
 *     wrote. If a later call has already overwritten it, the failing call
 *     leaves the cache alone — preventing a slow failing PATCH from
 *     rolling back a subsequent successful one.
 *
 * Cache:
 *   - Settings are user-controlled and change rarely except via the
 *     optimistic paths. `staleTime: 5 min` and
 *     `refetchOnWindowFocus: false` avoid redundant refetches.
 *
 * Error surface: `isError` reflects only the GET request. PATCH failures
 * surface as rejected setter promises; callers are responsible for
 * surfacing them (e.g. toast, retry UI).
 *
 * Scope: this hook manages `inputMode` and `chatLayoutPrefs`. `theme` is
 * owned by `ThemePreferenceContext`; consolidation is tracked as backlog
 * item D4 (issue #328). Both hooks read/write the same `profile.settings`
 * JSONB and the strict Zod schema (`ProfileSettingsSchema`) tolerates
 * partial keys, so they co-exist safely.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/useAuth';
import type { ChatLayoutMode, ChatLayoutPrefs, ProfileFields } from '@shared/v2-schemas';

const FIVE_MINUTES = 5 * 60 * 1000;

export interface ProfileSettings {
  inputMode?: 'voice' | 'text';
  theme?: 'auto' | 'light' | 'dark';
  chatLayoutPrefs?: ChatLayoutPrefs | null;
  // spec 033 — new preference flags (flat) + the namespaced profile block.
  // Mirrors `ProfileSettingsSchema` in `@shared/v2-schemas`.
  // (`reducedMotion` removed — ANA-311, was a no-op, no UI reads/writes it.)
  interfaceSounds?: boolean;
  profile?: ProfileFields;
  [key: string]: unknown;
}

export interface UseProfileSettingsResult {
  data: ProfileSettings | null;
  /** Convenience: `data?.chatLayoutPrefs ?? null` */
  chatLayoutPrefs: ChatLayoutPrefs | null;
  isLoading: boolean;
  isError: boolean;
  setMode: (mode: 'voice' | 'text') => Promise<void>;
  setChatLayoutPref: (
    bucket: 'overview' | 'other',
    mode: ChatLayoutMode | null,
  ) => Promise<void>;
  /**
   * Generic optimistic partial-update to `profiles.settings` (spec 033).
   * Deep-merges the `profile` block so a partial `{ profile: { jobTitle } }`
   * does not clobber sibling profile fields. Same rollback-on-error contract
   * as `setMode`/`setChatLayoutPref`.
   */
  patch: (partial: Partial<ProfileSettings>) => Promise<void>;
  /**
   * Writes the column-backed "Full name" via `PATCH /api/v1/profile` (spec
   * 033). Not part of `settings` jsonb. On success the profile-settings query
   * is invalidated; the cached identity (`useAuth`, kept in React state +
   * localStorage, not react-query) is refreshed by the caller via
   * `auth.validateSession()`. Rejects on failure so callers can surface a toast.
   */
  setFullName: (fullName: string) => Promise<void>;
}

const QUERY_KEY = ['v2', 'profile-settings'] as const;

function chatLayoutPrefsEqual(a: ChatLayoutPrefs | null | undefined, b: ChatLayoutPrefs | null | undefined) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export function useProfileSettings(): UseProfileSettingsResult {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const query = useQuery<ProfileSettings, Error>({
    queryKey: QUERY_KEY,
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: isAuthenticated,
    queryFn: async () => {
      const res = await fetch('/api/v1/profile/settings', { credentials: 'include' });
      if (!res.ok) throw new Error(`profile settings fetch failed: ${res.status}`);
      const body = (await res.json()) as { success?: boolean; data?: ProfileSettings };
      return body?.data ?? {};
    },
  });

  const chatLayoutPrefs = useMemo(
    () => query.data?.chatLayoutPrefs ?? null,
    [query.data?.chatLayoutPrefs],
  );

  const setMode = useCallback(
    async (mode: 'voice' | 'text') => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<ProfileSettings>(QUERY_KEY) ?? {};
      const optimistic: ProfileSettings = { ...previous, inputMode: mode };
      queryClient.setQueryData<ProfileSettings>(QUERY_KEY, optimistic);

      try {
        const res = await fetch('/api/v1/profile/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ inputMode: mode }),
        });
        if (!res.ok) throw new Error(`profile settings PATCH failed: ${res.status}`);
      } catch (err) {
        const current = queryClient.getQueryData<ProfileSettings>(QUERY_KEY);
        if (current?.inputMode === optimistic.inputMode) {
          queryClient.setQueryData<ProfileSettings>(QUERY_KEY, previous);
        }
        throw err;
      }
    },
    [queryClient],
  );

  const setChatLayoutPref = useCallback(
    async (bucket: 'overview' | 'other', mode: ChatLayoutMode | null) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<ProfileSettings>(QUERY_KEY) ?? {};
      const prevPrefs = previous.chatLayoutPrefs ?? {};
      const nextPrefs: ChatLayoutPrefs = { ...prevPrefs, [bucket]: mode };
      const optimistic: ProfileSettings = { ...previous, chatLayoutPrefs: nextPrefs };
      queryClient.setQueryData<ProfileSettings>(QUERY_KEY, optimistic);

      try {
        const res = await fetch('/api/v1/profile/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ chatLayoutPrefs: nextPrefs }),
        });
        if (!res.ok) throw new Error(`profile settings PATCH failed: ${res.status}`);
      } catch (err) {
        const current = queryClient.getQueryData<ProfileSettings>(QUERY_KEY);
        if (chatLayoutPrefsEqual(current?.chatLayoutPrefs, optimistic.chatLayoutPrefs)) {
          queryClient.setQueryData<ProfileSettings>(QUERY_KEY, previous);
        }
        throw err;
      }
    },
    [queryClient],
  );

  const patch = useCallback(
    async (partial: Partial<ProfileSettings>) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previous = queryClient.getQueryData<ProfileSettings>(QUERY_KEY) ?? {};

      // Deep-merge the `profile` block so a partial profile update keeps the
      // sibling fields; everything else is a shallow merge (matching the
      // server's PATCH semantics in contracts/profile-settings-api.md).
      const optimistic: ProfileSettings = { ...previous, ...partial };
      if (partial.profile) {
        optimistic.profile = { ...(previous.profile ?? {}), ...partial.profile };
      }
      queryClient.setQueryData<ProfileSettings>(QUERY_KEY, optimistic);

      // Send the same shape we merged so the server applies an identical patch.
      const body: Partial<ProfileSettings> = { ...partial };
      if (partial.profile) body.profile = optimistic.profile;

      try {
        const res = await fetch('/api/v1/profile/settings', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`profile settings PATCH failed: ${res.status}`);
      } catch (err) {
        // Only roll back if our optimistic write is still the cached value;
        // a later successful write must not be reverted by a slow failure.
        const current = queryClient.getQueryData<ProfileSettings>(QUERY_KEY);
        if (JSON.stringify(current) === JSON.stringify(optimistic)) {
          queryClient.setQueryData<ProfileSettings>(QUERY_KEY, previous);
        }
        throw err;
      }
    },
    [queryClient],
  );

  const setFullName = useCallback(
    async (fullName: string) => {
      const res = await fetch('/api/v1/profile', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ fullName }),
      });
      if (!res.ok) throw new Error(`profile PATCH failed: ${res.status}`);
      // Full name lives on the profile row, not in settings jsonb. Invalidate
      // the profile-settings query; the cached identity (`useAuth`) is refreshed
      // by the caller via `auth.validateSession()` — the AuthProvider keeps the
      // user in React state + localStorage, not under the ['auth','me'] key.
      await queryClient.invalidateQueries({ queryKey: ['v2', 'profile-settings'] });
    },
    [queryClient],
  );

  return {
    data: query.data ?? null,
    chatLayoutPrefs,
    isLoading: query.isLoading,
    isError: query.isError,
    setMode,
    setChatLayoutPref,
    patch,
    setFullName,
  };
}
